import { Component, type ReactNode } from 'react';

import i18n from '@/i18n';

interface State {
  error: Error | null;
}

export default class ErrorBoundary extends Component<
  { children: ReactNode },
  State
> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: unknown) {
    console.error('ErrorBoundary:', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-4">
          <div className="max-w-lg w-full bg-[var(--bg-elevated)] border border-red-300 dark:border-red-800 rounded-lg p-6 shadow-lg">
            <h1 className="text-lg font-semibold text-red-700 dark:text-red-300 mb-2">
              {i18n.t('shell_ui.error_boundary.title')}
            </h1>
            <p className="text-sm text-[var(--text-muted)] mb-3">
              {i18n.t('shell_ui.error_boundary.description')}
            </p>
            <pre className="text-[11px] font-mono bg-[var(--bg-surface)] border border-[var(--border-strong)] rounded p-3 overflow-auto max-h-60 whitespace-pre-wrap">
              {this.state.error.message}
              {'\n\n'}
              {this.state.error.stack}
            </pre>
            <div className="mt-4 flex gap-2">
              <button
                onClick={() => this.setState({ error: null })}
                className="btn-outline text-sm"
              >
                {i18n.t('common.retry')}
              </button>
              <button
                onClick={() => window.location.reload()}
                className="btn-primary text-sm"
              >
                {i18n.t('shell_ui.error_boundary.reload')}
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
